import { Fragment, useEffect, useState } from 'react'
import { Info as InfoIcon } from 'react-feather'
import { Controller, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Autocomplete } from '@material-ui/lab'
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { Label, Select as SelectComponent } from 'components'

import dataLifeCycleSchema from '../schema'
import helpers from 'helpers'

import useDataProcess from 'hooks/useDataProcess'
import useAssetInventory from 'hooks/useAssetInventory'

import useStyles from './styles'
import constants from 'constants/index'

const Form = ({ onSubmit, dataLifeCycle, dataProcess }) => {
  const {
    STORY_MODE_PERMANENT_TYPE,
    STORAGE_DEFINED_TYPE,
    STORAGE_UNDEFINED_TYPE,
    STORAGE_PERMANENT_TYPE,
  } = constants.dataProcess

  const volumetryOptions = helpers.dataProcesses.selectVolumetryOptions()

  const [appliedDefaultValues, setAppliedDefaultValues] = useState(false)
  const [hasNewAssetInventory, setHasNewAssetInventory] = useState(false)

  const {
    internalAllocationModes,
    discardModes,
    recoveries,
    protections,
    data: dataProcessData,
  } = useDataProcess()

  const {
    assetInventories,
    loadingInventories,
    assetInventoryCategories,
    loadingCategories,
    setAssetInventoryProps,
  } = useAssetInventory()

  const storageMode = dataProcess?.storageMode
  const storageTypeFormated = dataLifeCycle?.storageType

  const filterAssetInventoryCategories = assetInventoryCategories.map(
    (category) => ({
      id: category.id,
      name: category.name,
    }),
  )

  const filterAssetInventories = assetInventories.map((inventory) => ({
    id: inventory.id,
    name: inventory.name,
  }))

  const defaultAssetInventory = filterAssetInventories.find(
    (inventory) => inventory.id === dataLifeCycle?.assetInventory?.id,
  )

  const defaultAssetInventoryCategory = filterAssetInventoryCategories.find(
    (category) =>
      category.id === dataLifeCycle?.assetInventoryCategory?.id ||
      category.id === dataLifeCycle?.assetInventory?.assetInventoryCategory?.id,
  )

  const classes = useStyles()
  const { handleSubmit, errors, watch, control, setError, setValue } = useForm({
    validationSchema: dataLifeCycleSchema({ hasNewAssetInventory }),
    defaultValues: {
      assetInventory: defaultAssetInventory || null,
      assetInventoryCategory: defaultAssetInventoryCategory || null,
      internalAllocationModeId: dataLifeCycle?.internalAllocationMode?.id || '',
      storageType: `${storageTypeFormated}` ?? '',
      value: dataLifeCycle?.value || '',
      volumetry: dataLifeCycle?.volumetry || '',
      retentionFinality: dataLifeCycle?.retentionFinality || '',
      discardModeId: dataLifeCycle?.discardMode?.id || '',
      recoveryId: dataLifeCycle?.recovery?.id || '',
      protectionId: dataLifeCycle?.protection?.id || '',
      anotherDiscardMode: dataLifeCycle?.discardMode?.id || '',
      anotherRecovery: dataLifeCycle?.recovery?.id || '',
    },
  })

  const assetInventoryCategory = watch('assetInventoryCategory')

  const handleAnotherAssetInventory = () => {
    setAppliedDefaultValues(false)
    setHasNewAssetInventory((prevState) => !prevState)
  }

  // Realiza a busca do ativo de acordo com a categoria selecionada
  useEffect(() => {
    if (!hasNewAssetInventory) {
      setValue('assetInventory', null)

      if (assetInventoryCategory?.id) {
        setAssetInventoryProps({
          minimal: true,
          assetInventoryCategoryId: assetInventoryCategory?.id,
          companyId: dataProcessData?.company?.id,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetInventoryCategory])

  // Aplica os valores padrões ao carregar a busca dos ativos
  useEffect(() => {
    if (!appliedDefaultValues && !!defaultAssetInventory) {
      setAppliedDefaultValues(true)
      setValue('assetInventory', defaultAssetInventory)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAssetInventory])

  // Limpa os filtros ao desmontar o componente para forçar a re-consulta
  useEffect(() => {
    return () => {
      setAppliedDefaultValues(false)
      setAssetInventoryProps({})
    }
  }, [])

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data, watch, setError))}
      id="data-life-cycle-form"
    >
      <Grid container spacing={2} className={classes.root}>
        <Grid item md={6} xs={12} className={classes.gridItem}>
          <Controller control={control} name="dataLifeCycleId" as={<></>} />
          {!hasNewAssetInventory && (
            <Fragment>
              <Controller
                control={control}
                mode="onChange"
                name="assetInventoryCategory"
                onChange={([, data]) => data || null}
                as={
                  <Autocomplete
                    options={filterAssetInventoryCategories}
                    getOptionLabel={(option) => option.name || ''}
                    getOptionSelected={(option, value) =>
                      option.id === value.id || value.id === 0
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Categoria do ativos"
                        placeholder="Em qual categoria do ativos esse salvaguarda se enquadra?"
                        error={!!errors.assetInventoryCategory}
                        helperText={
                          errors?.assetInventoryCategory?.message ||
                          errors?.assetInventoryCategory?.id?.message
                        }
                        variant="outlined"
                      />
                    )}
                    loading={loadingCategories}
                  />
                }
              />
              {!!assetInventoryCategory?.id && (
                <Controller
                  control={control}
                  mode="onChange"
                  name="assetInventory"
                  onChange={([, data]) => data || null}
                  as={
                    <Autocomplete
                      options={filterAssetInventories}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ativos"
                          placeholder="Em qual ativo esse salvaguarda se enquadra?"
                          error={!!errors.assetInventory}
                          helperText={
                            errors?.assetInventory?.message ||
                            errors?.assetInventory?.id?.message
                          }
                        />
                      )}
                      loading={loadingInventories}
                    />
                  }
                />
              )}
            </Fragment>
          )}
          {hasNewAssetInventory && (
            <Controller
              control={control}
              name="anotherAssetInventory"
              as={
                <TextField
                  label="Nome do novo inventário de ativos"
                  placeholder="Defina o nome do novo inventário de ativos"
                  variant="outlined"
                  error={!!errors.anotherAssetInventory}
                  helperText={errors?.anotherAssetInventory?.message}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Recomenda-se editar posteriormente esse inventário na tela de inventários de ativos para completar as informações necessárias.">
                          <IconButton
                            className={classes.infoIcon}
                            color="primary"
                            size="small"
                          >
                            <InfoIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              }
            />
          )}
          <Typography
            variant="caption"
            color="secondary"
            className={classes.link}
            onClick={handleAnotherAssetInventory}
          >
            {hasNewAssetInventory
              ? 'Voltar para a seleção de ativos existentes'
              : 'Não encontrei os ativos desejados'}
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <Controller
            name="internalAllocationModeId"
            control={control}
            as={
              <SelectComponent
                items={internalAllocationModes}
                label="Ambiente de alocação"
                error={!!errors.internalAllocationModeId}
                helperText={
                  errors.internalAllocationModeId?.message ||
                  'A informação é alocada em que ambiente?'
                }
              />
            }
            mode="onBlur"
          />
        </Grid>
        <Grid item xs={12}>
          <Box mt={1}>
            <Label
              title="Tempo de armazenamento"
              description="Por quanto tempo esses dados serão armazenados"
              item
            >
              <Controller
                control={control}
                name="storageType"
                as={
                  <RadioGroup>
                    <FormControlLabel
                      key={`${STORAGE_DEFINED_TYPE}`}
                      value={`${STORAGE_DEFINED_TYPE}`}
                      control={<Radio />}
                      label="Definido"
                    />
                    <FormControlLabel
                      key={`${STORAGE_UNDEFINED_TYPE}`}
                      value={`${STORAGE_UNDEFINED_TYPE}`}
                      control={<Radio />}
                      label="Indefinido"
                    />
                    {storageMode === STORY_MODE_PERMANENT_TYPE && (
                      <FormControlLabel
                        key={`${STORAGE_PERMANENT_TYPE}`}
                        value={`${STORAGE_PERMANENT_TYPE}`}
                        control={<Radio />}
                        label="Permanente"
                      />
                    )}
                  </RadioGroup>
                }
                mode="onChange"
              />
            </Label>
            {watch('storageType') === `${STORAGE_DEFINED_TYPE}` && (
              <>
                <Grid container spacing={1}>
                  <Grid item lg={6} xs={12}>
                    <Label
                      title="Valor"
                      description="Qual é a quantidade do tempo?"
                      item
                    >
                      <Controller
                        name="value"
                        control={control}
                        as={
                          <TextField
                            type="number"
                            variant="outlined"
                            error={!!errors.value}
                            fullWidth
                            helperText={
                              errors?.value?.message ||
                              errors?.value?.types?.type?.invalid
                            }
                          />
                        }
                        mode="onBlur"
                        onChange={([e]) =>
                          e.target.value < 0 ? '0' : e.target.value
                        }
                      />
                    </Label>
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <Label
                      title="Unidade de tempo"
                      description="Qual é o formato do tempo?"
                      item
                    >
                      <Controller
                        name="volumetry"
                        control={control}
                        as={
                          <SelectComponent
                            items={volumetryOptions}
                            error={!!errors.volumetry}
                            fullWidth
                            helperText={
                              errors.volumetry?.message ||
                              errors?.value?.types?.type?.invalid
                            }
                          />
                        }
                        mode="onBlur"
                      />
                    </Label>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption">
                      *Atenção: o tempo informado deve ser menor ou igual ao das
                      informações gerais do processo
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Grid>

        {/*  */}
        <Grid item xs={12}>
          <Controller
            name="retentionFinality"
            control={control}
            as={
              <TextField
                label="Justificativa de retenção"
                multiline
                rows={3}
                color="primary"
                variant="outlined"
                error={!!errors.retentionFinality}
                helperText={
                  errors.retentionFinality?.message ||
                  'Justifique porque o registro é retido no departamento pelo tempo informado.'
                }
              />
            }
            mode="onChange"
            fullWidth
          />
        </Grid>

        {watch('storageType') !== `${STORAGE_PERMANENT_TYPE}` && (
          <Grid item xs={12}>
            <Controller
              name="discardModeId"
              control={control}
              as={
                <SelectComponent
                  items={discardModes}
                  label="Como os dados são dispostos?"
                  color="primary"
                  anotherOption
                  error={!!errors.discardModeId}
                  helperText={
                    errors.discardModeId?.message ||
                    'Modo que os dados são dispostos após a utilização.'
                  }
                  fullWidth
                />
              }
              mode="onBlur"
            />
            {watch('discardModeId') === 'another' && (
              <Box mt={1}>
                <Controller
                  name="anotherDiscardMode"
                  control={control}
                  as={
                    <TextField
                      label="Outro: como os dados são dispostos?"
                      color="primary"
                      variant="outlined"
                      error={!!errors.anotherDiscardMode}
                      helperText={errors.anotherDiscardMode?.message}
                      fullWidth
                    />
                  }
                />
              </Box>
            )}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <Controller
            name="recoveryId"
            control={control}
            as={
              <SelectComponent
                items={recoveries}
                label="Forma de recuperação"
                color="primary"
                anotherOption
                error={!!errors.recoveryId}
                helperText={
                  errors.recoveryId?.message ||
                  'Qual dado é utilizado para localizar e acessar/recuperar o registro?'
                }
              />
            }
            mode="onBlur"
          />
          {watch('recoveryId') === 'another' && (
            <Box mt={1}>
              <Controller
                name="anotherRecovery"
                control={control}
                as={
                  <TextField
                    label="Outro: forma de recuperação"
                    color="primary"
                    variant="outlined"
                    error={!!errors.anotherRecovery}
                    helperText={errors.anotherRecovery?.message}
                  />
                }
              />
            </Box>
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          <Controller
            name="protectionId"
            control={control}
            as={
              <SelectComponent
                items={protections}
                label="Forma de proteção"
                color="primary"
                error={!!errors.protectionId}
                helperText={
                  errors.protectionId?.message ||
                  'A informação é protegida fisicamente(impressa) ou eletronicamente (sistemas, e-mail, servidor,etc)?'
                }
                name="protectionId"
              />
            }
            mode="onBlur"
          />
        </Grid>
      </Grid>
    </form>
  )
}

Form.propTypes = {
  onSubmit: PropTypes.func,
  dataLifeCycle: PropTypes.object,
}

Form.defaultProps = {
  onSubmit: () => {},
  dataLifeCycle: {},
}

export default Form
